/**
 * Phase 2A — Care-Plan Template Manager (admin).
 *
 * CRUD + lifecycle (Draft/Active/Inactive/Archived) + immutable versioning +
 * duplication + preview for care-plan templates. Flag-gated: when
 * `participant_careplan_template_management_enabled` is OFF the endpoints 404
 * and this page shows a disabled notice.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Plus, Pencil, Eye, Copy, CheckCircle2, PauseCircle, Archive, RotateCcw, GitBranch, Layers,
} from 'lucide-react';

const CATEGORIES = ['SIL', 'SDA', 'PAEDIATRIC', 'RESPITE', 'CENTRE_BASED', 'COMMUNITY', 'GENERIC', 'CUSTOM'];
const LIFECYCLES = ['DRAFT', 'ACTIVE', 'INACTIVE', 'ARCHIVED'];

const LIFECYCLE_STYLE = {
  DRAFT: 'bg-slate-100 text-slate-700',
  ACTIVE: 'bg-emerald-100 text-emerald-700',
  INACTIVE: 'bg-amber-100 text-amber-700',
  ARCHIVED: 'bg-red-100 text-red-700',
};

const EMPTY = {
  name: '', description: '', category: 'CUSTOM', template_type: 'STRUCTURED',
  scope_level: 'ORG', branch_id: '', review_cycle_months: 12,
  sections: [], structured: { sections: [] },
};

export default function CarePlanTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [search, setSearch] = useState('');
  const [lifecycle, setLifecycle] = useState('ALL');
  const [category, setCategory] = useState('ALL');
  const [editor, setEditor] = useState(null);        // {mode:'create'|'edit', data}
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ include_all_versions: 'true' });
      if (lifecycle !== 'ALL') params.set('lifecycle', lifecycle);
      if (category !== 'ALL') params.set('category', category);
      if (search) params.set('search', search);
      const r = await api.get(`/careplan-templates?${params.toString()}`);
      setTemplates(r.data.data || []);
      setDisabled(false);
    } catch (e) {
      if (e?.response?.status === 404) { setDisabled(true); setTemplates([]); }
      else toast.error(getErrorMessage(e));
    } finally { setLoading(false); }
  }, [lifecycle, category, search]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    const d = editor.data;
    const payload = {
      name: d.name, description: d.description, category: d.category,
      template_type: d.template_type, review_cycle_months: Number(d.review_cycle_months) || 12,
      sections: d.sections || [], structured: d.structured || { sections: [] },
    };
    if (editor.mode === 'create') {
      payload.scope_level = d.scope_level;
      if (d.scope_level === 'BRANCH') payload.branch_id = d.branch_id;
    }
    try {
      if (editor.mode === 'create') await api.post('/careplan-templates', payload);
      else await api.put(`/careplan-templates/${d.id}`, payload);
      toast.success(`Template ${editor.mode === 'create' ? 'created' : 'updated'}`);
      setEditor(null); await load();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const act = async (t, action, label) => {
    try {
      await api.post(`/careplan-templates/${t.id}/${action}`, { reason: `${label} via admin` });
      toast.success(`Template ${label.toLowerCase()}`);
      await load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const duplicate = async (t) => {
    try { await api.post(`/careplan-templates/${t.id}/duplicate`, {}); toast.success('Template duplicated'); await load(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };

  const openPreview = async (t) => {
    try { const r = await api.get(`/careplan-templates/${t.id}/preview`); setPreview(r.data.data); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };

  const filtered = templates;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="careplan-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Care-Plan Templates</h1>
          <p className="text-muted-foreground mt-1">Govern templates used to create participant care plans</p>
        </div>
        {!disabled && (
          <Button onClick={() => setEditor({ mode: 'create', data: { ...EMPTY } })} data-testid="template-create-button">
            <Plus className="h-4 w-4 mr-1" /> New Template
          </Button>
        )}
      </div>

      {disabled ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground" data-testid="template-feature-disabled">
          Template management is currently disabled. Enable
          <span className="font-mono text-xs mx-1">participant_careplan_template_management_enabled</span>
          in Admin → Feature Flags to manage care-plan templates.
        </CardContent></Card>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            <Input placeholder="Search templates" value={search} onChange={e => setSearch(e.target.value)}
                   className="w-56" data-testid="template-search" />
            <Select value={lifecycle} onValueChange={setLifecycle}>
              <SelectTrigger className="w-40" data-testid="template-filter-lifecycle"><SelectValue placeholder="Lifecycle" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                {LIFECYCLES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-40" data-testid="template-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All categories</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <p className="text-muted-foreground py-10 text-center">Loading…</p>
          ) : filtered.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground" data-testid="template-empty">
              No templates found. Create your first template.
            </CardContent></Card>
          ) : (
            <div className="grid gap-3">
              {filtered.map(t => (
                <Card key={t.id} data-testid={`template-row-${t.id}`}>
                  <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{t.name}</span>
                        <Badge className={`text-[10px] ${LIFECYCLE_STYLE[t.lifecycle_status] || ''}`}>{t.lifecycle_status}</Badge>
                        <Badge variant="outline" className="text-[10px]">v{t.version}</Badge>
                        <Badge variant="secondary" className="text-[10px]">{t.category}</Badge>
                        <Badge variant="outline" className="text-[10px]">{t.template_type}</Badge>
                        <Badge variant="outline" className="text-[10px]">{t.scope_level}{t.scope_level === 'BRANCH' && t.branch_id ? `:${t.branch_id}` : ''}</Badge>
                        {t.usage_count > 0 && <Badge variant="outline" className="text-[10px]">used {t.usage_count}×</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{t.description || '—'} · review {t.review_cycle_months}mo</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 flex-wrap">
                      <Button size="sm" variant="ghost" onClick={() => openPreview(t)} data-testid={`template-preview-${t.id}`}><Eye className="h-3 w-3" /></Button>
                      {t.lifecycle_status === 'DRAFT' && (
                        <Button size="sm" variant="ghost" onClick={() => setEditor({ mode: 'edit', data: { ...EMPTY, ...t } })} data-testid={`template-edit-${t.id}`}><Pencil className="h-3 w-3" /></Button>
                      )}
                      {t.lifecycle_status === 'ACTIVE' && (
                        <Button size="sm" variant="ghost" onClick={() => setEditor({ mode: 'edit', data: { ...EMPTY, ...t } })} title="Metadata-safe edit" data-testid={`template-edit-${t.id}`}><Pencil className="h-3 w-3" /></Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => duplicate(t)} data-testid={`template-duplicate-${t.id}`}><Copy className="h-3 w-3" /></Button>
                      {t.lifecycle_status === 'ACTIVE' && (
                        <Button size="sm" variant="ghost" onClick={() => act(t, 'new-version', 'New version')} title="Cut new version" data-testid={`template-newversion-${t.id}`}><GitBranch className="h-3 w-3" /></Button>
                      )}
                      {(t.lifecycle_status === 'DRAFT' || t.lifecycle_status === 'INACTIVE') && (
                        <Button size="sm" variant="ghost" onClick={() => act(t, 'activate', 'Activated')} className="text-emerald-600" data-testid={`template-activate-${t.id}`}><CheckCircle2 className="h-3 w-3" /></Button>
                      )}
                      {t.lifecycle_status === 'ACTIVE' && (
                        <Button size="sm" variant="ghost" onClick={() => act(t, 'retire', 'Retired')} className="text-amber-600" data-testid={`template-retire-${t.id}`}><PauseCircle className="h-3 w-3" /></Button>
                      )}
                      {t.lifecycle_status === 'ARCHIVED' ? (
                        <Button size="sm" variant="ghost" onClick={() => act(t, 'restore', 'Restored')} data-testid={`template-restore-${t.id}`}><RotateCcw className="h-3 w-3" /></Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => act(t, 'archive', 'Archived')} className="text-red-600" data-testid={`template-archive-${t.id}`}><Archive className="h-3 w-3" /></Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Editor dialog */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-lg" data-testid="template-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editor?.mode === 'create' ? 'New Template' : `Edit ${editor?.data?.name || ''}`}</DialogTitle>
            <DialogDescription>
              {editor?.data?.lifecycle_status === 'ACTIVE'
                ? 'This template is ACTIVE — only metadata-safe fields can be edited. Use "New version" to change content.'
                : 'Draft templates are fully editable.'}
            </DialogDescription>
          </DialogHeader>
          {editor && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Name</Label>
                <Input value={editor.data.name} onChange={e => setEditor(s => ({ ...s, data: { ...s.data, name: e.target.value } }))} data-testid="template-name-input" />
              </div>
              <div className="space-y-1">
                <Label>Description</Label>
                <Textarea rows={2} value={editor.data.description || ''} onChange={e => setEditor(s => ({ ...s, data: { ...s.data, description: e.target.value } }))} data-testid="template-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Category</Label>
                  <Select value={editor.data.category} onValueChange={v => setEditor(s => ({ ...s, data: { ...s.data, category: v } }))}>
                    <SelectTrigger data-testid="template-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Review cycle (months)</Label>
                  <Input type="number" min={1} max={60} value={editor.data.review_cycle_months} onChange={e => setEditor(s => ({ ...s, data: { ...s.data, review_cycle_months: e.target.value } }))} data-testid="template-review-input" />
                </div>
              </div>
              {editor.mode === 'create' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Type</Label>
                    <Select value={editor.data.template_type} onValueChange={v => setEditor(s => ({ ...s, data: { ...s.data, template_type: v } }))}>
                      <SelectTrigger data-testid="template-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="STRUCTURED">STRUCTURED</SelectItem><SelectItem value="SIMPLE">SIMPLE</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Scope</Label>
                    <Select value={editor.data.scope_level} onValueChange={v => setEditor(s => ({ ...s, data: { ...s.data, scope_level: v } }))}>
                      <SelectTrigger data-testid="template-scope-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="ORG">ORG</SelectItem><SelectItem value="BRANCH">BRANCH</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              {editor.mode === 'create' && editor.data.scope_level === 'BRANCH' && (
                <div className="space-y-1">
                  <Label>Branch ID</Label>
                  <Input value={editor.data.branch_id} onChange={e => setEditor(s => ({ ...s, data: { ...s.data, branch_id: e.target.value } }))} data-testid="template-branch-input" />
                </div>
              )}
              <SectionEditor editor={editor} setEditor={setEditor} />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditor(null)}>Cancel</Button>
            <Button disabled={busy || !editor?.data?.name} onClick={save} data-testid="template-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg" data-testid="template-preview-dialog">
          <DialogHeader>
            <DialogTitle>{preview?.name} · v{preview?.version}</DialogTitle>
            <DialogDescription>{preview?.category} · {preview?.template_type} · review {preview?.review_cycle_months}mo</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-auto">
            {(preview?.sections_structured || []).map((s, i) => (
              <div key={i} className="flex justify-between border rounded px-3 py-2 text-sm">
                <span>{s.title || s.section_key}</span><span className="text-muted-foreground">{(s.rows || []).length} rows</span>
              </div>
            ))}
            {(preview?.sections_simple || []).map((s, i) => (
              <div key={`s${i}`} className="border rounded px-3 py-2 text-sm">
                <span className="font-medium">{s.label || s.key}</span>
                <p className="text-muted-foreground text-xs mt-1 whitespace-pre-wrap">{s.content || '—'}</p>
              </div>
            ))}
            {(preview?.sections_structured || []).length === 0 && (preview?.sections_simple || []).length === 0 && (
              <p className="text-sm text-muted-foreground italic">No sections defined.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Minimal section editor: SIMPLE → key/label/content rows; STRUCTURED → section_key/title rows.
function SectionEditor({ editor, setEditor }) {
  const isActive = editor.data.lifecycle_status === 'ACTIVE';
  const isSimple = editor.data.template_type === 'SIMPLE';
  const list = isSimple ? (editor.data.sections || []) : ((editor.data.structured?.sections) || []);

  const setList = (next) => setEditor(s => ({
    ...s,
    data: isSimple
      ? { ...s.data, sections: next }
      : { ...s.data, structured: { ...(s.data.structured || {}), sections: next } },
  }));

  if (isActive) {
    return <p className="text-xs text-muted-foreground italic" data-testid="section-editor-locked">Section content is locked on ACTIVE templates. Cut a new version to edit.</p>;
  }

  return (
    <div className="space-y-2 border-t pt-2" data-testid="section-editor">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1"><Layers className="h-3 w-3" /> Sections</Label>
        <Button size="sm" variant="outline" onClick={() => setList([...list, isSimple ? { key: '', label: '', content: '' } : { section_key: '', title: '', rows: [] }])} data-testid="section-add">
          <Plus className="h-3 w-3 mr-1" /> Add section
        </Button>
      </div>
      {list.length === 0 && <p className="text-xs text-muted-foreground italic">No sections yet.</p>}
      {list.map((sec, i) => (
        <div key={i} className="grid grid-cols-2 gap-2 items-start border rounded p-2" data-testid={`section-item-${i}`}>
          <Input placeholder={isSimple ? 'key' : 'section_key'} value={isSimple ? sec.key : sec.section_key}
                 onChange={e => { const n = [...list]; n[i] = { ...sec, [isSimple ? 'key' : 'section_key']: e.target.value }; setList(n); }} />
          <Input placeholder={isSimple ? 'label' : 'title'} value={isSimple ? sec.label : sec.title}
                 onChange={e => { const n = [...list]; n[i] = { ...sec, [isSimple ? 'label' : 'title']: e.target.value }; setList(n); }} />
          {isSimple && (
            <Textarea className="col-span-2" rows={2} placeholder="content" value={sec.content || ''}
                      onChange={e => { const n = [...list]; n[i] = { ...sec, content: e.target.value }; setList(n); }} />
          )}
          <button type="button" className="col-span-2 text-xs text-red-600 text-left" onClick={() => setList(list.filter((_, j) => j !== i))} data-testid={`section-remove-${i}`}>Remove section</button>
        </div>
      ))}
    </div>
  );
}
